/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { formatISO, addDays } from 'date-fns';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import { parseDate, hasNoAccess, exportCsvApi } from '@global/helpers';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import ModalFull from '@components/modal/ModalFull';
import DatePicker from '@components/form/DatePicker';
import StatusPicker from '@components/form/StatusPicker';
import ActionButton from '@components/navbar/ActionButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetBudgetPocketListQuery,
  useGetBudgetPocketCountQuery,
  useGetBudgetPocketInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  name: {
    head: 'Budget Name',
    body: 'name',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  value: { head: 'Budget Value', body: 'value', type: 'currency' },
  budget_remaining: {
    head: 'Budget Remaining',
    body: 'budget_remaining',
    type: 'currency',
  },
  periode: { head: 'Period', body: 'periode' },
  active: { head: 'Active', body: 'is_active', type: 'status_active' },
  current_status: { head: 'Status', body: 'current_status', type: 'status' },
  last_updated: { head: 'Last Updated, By', body: 'last_updated' },
};

const STATUSES = [
  { name: 'Approved', value: 'approved' },
  { name: 'Draft', value: 'draft' },
  { name: 'Pending Approval', value: 'need_approval' },
  { name: 'Rejected', value: 'rejected' },
];

// Search-only list: the search box writes `name`; unwrap it into a $regex query.
const toApiFilter = (raw = {}) => {
  const filter = {};
  const name = raw.name?.$regex ?? raw.name;
  if (name) {
    filter.name = { $regex: name, $options: 'i' };
  }
  return filter;
};

// Display row (RTK cache frozen → new object): period + last-updated strings.
const mapRow = (item) => ({
  ...item,
  id: item._id,
  periode: `${item.start_date ? parseDate(item.start_date) : '-'} - ${
    item.end_date ? parseDate(item.end_date) : '-'
  }`,
  last_updated: `${item.updated_at ? parseDate(item.updated_at) : '-'}${
    item?.updated_by?.name ? `, ${item.updated_by.name}` : ''
  }`,
});

const BudgetPocketScreen = (props) => {
  // ── selectors ──
  const dispatch = useDispatch();
  const showBudgetExport = useSelector((state) => state.modal.showBudgetExport);

  // ── local state ──
  const [exporting, setExporting] = useState(false);
  const [filterExport, setFilterExport] = useState({
    current_status: '',
    period: { $gte: '', $lte: '' },
  });

  // ── derived ──
  const hasAccess = !hasNoAccess(['superadmin', 'marketing_admin', 'general']);
  const hasExportAccess = !hasNoAccess([
    'superadmin',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'general_manager_marketing',
    'marketing_admin',
    'general',
    'regional_distributor_manager',
  ]);

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetBudgetPocketListQuery,
    countHook: useGetBudgetPocketCountQuery,
    infiniteHook: useGetBudgetPocketInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── memoized values ──
  const searchValue = useMemo(() => {
    const k = params.filter?.name;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map(mapRow);
  }, [list.data]);

  // ── handlers ──
  const goToForm = () => {
    openChild('BudgetPocketFormScreen');
  };

  const gotToDetail = (data) => {
    openChild('BudgetPocketDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data.id,
    });
  };

  const onExport = async () => {
    setExporting(true);
    try {
      if (!filterExport.current_status) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter status harus diisi',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      if (!filterExport.period?.$gte || !filterExport.period?.$lte) {
        dispatch(
          showAlert({
            show: true,
            title: 'Error',
            description: 'Filter date harus diisi',
            isConfirm: false,
          }),
        );
        setExporting(false);
        return;
      }
      const payload = {
        current_status: filterExport.current_status.value,
        period: filterExport.period,
      };
      await exportCsvApi('budget-pockets', payload, {
        isDownload: false,
        message: 'Export in progress, the result will be sent to your email.',
      });
      setExporting(false);
    } catch (error) {
      console.log('error-export', error);
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'A system error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  // ── effects ──
  // Own the header so the mobile Add goes through openChild (preserves the list
  // on return) rather than the navigator's plain navigate.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () =>
        isWeb ? (
          <NotificationSettingButton {...props} />
        ) : hasAccess ? (
          <ActionButton
            icon={require('@assets/img/icons/plus-white.svg')}
            onCallback={goToForm}
          />
        ) : (
          false
        ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccess]);

  // ── render ──
  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Budget Pocket">
            <View style={{ flexDirection: 'row' }}>
              {hasExportAccess ? (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 90, marginRight: 10 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      Export
                    </SociollaText>
                  }
                  onPress={() => {
                    dispatch(
                      showPopup({ name: 'showBudgetExport', value: true }),
                    );
                  }}
                />
              ) : (
                false
              )}
              {hasAccess ? (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => goToForm()}
                />
              ) : (
                false
              )}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            searchKey="name"
            searchValue={searchValue}
            searchPlaceholder="Search by name..."
            hasCallback={true}
            hasNoHide={true}
            showFilter={false}
            onClickRow={({ data }) => gotToDetail(data)}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          searchValue={searchValue}
          placeholder="Search by name"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              left={true}
              onPress={() => gotToDetail(data)}>
              <SociollaText type="medium">{data.name}</SociollaText>
            </Layout.ItemMobile>
          )}
        />
      )}
      <ModalFull
        title="Export Budget Pocket"
        noTranslate={true}
        show={showBudgetExport}
        onClose={() =>
          dispatch(showPopup({ name: 'showBudgetExport', value: false }))
        }>
        <Layout.FormWrapper style={{ padding: 15 }}>
          <Layout.FormGroup lastItem>
            <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
              Date
            </SociollaText>
            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}>
              <DatePicker
                label="Start Date"
                name="created_at"
                style={{ width: '49%' }}
                value={filterExport.period?.$gte}
                onChange={(value) => {
                  const val = {
                    ...filterExport.period,
                    $gte: formatISO(value),
                  };
                  setFilterExport({ ...filterExport, period: val });
                }}
              />
              <DatePicker
                label="End Date"
                name="created_at"
                style={{ width: '49%' }}
                value={filterExport.period?.$lte}
                disableBeforeDate={
                  filterExport.period?.$gte
                    ? filterExport.period.$gte
                    : addDays(new Date(), 1)
                }
                onChange={(value) => {
                  const val = {
                    ...filterExport.period,
                    $lte: formatISO(value),
                  };
                  setFilterExport({ ...filterExport, period: val });
                }}
              />
            </View>
          </Layout.FormGroup>
          <Layout.FormGroup>
            <StatusPicker
              label="Status"
              options={STATUSES}
              value={filterExport.current_status}
              trackBy="value"
              labelKey="name"
              minWidth={80}
              onChange={(val) => {
                setFilterExport({ ...filterExport, current_status: val });
              }}
            />
          </Layout.FormGroup>
          <Layout.FormGroup>
            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'center',
                marginTop: 15,
              }}>
              <Button
                rounded={true}
                type="primary"
                buttonStyle={{ width: 90 }}
                disabledStyle={{ width: 90 }}
                disabled={exporting}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    {exporting ? 'Loading...' : 'Export'}
                  </SociollaText>
                }
                onPress={() => onExport()}
              />
            </View>
          </Layout.FormGroup>
        </Layout.FormWrapper>
      </ModalFull>
    </Layout.Container>
  );
};

export default BudgetPocketScreen;
